import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "../../app-toast";
import {
  getPharmacyNotifications,
  markPharmacyNotificationRead,
  deletePharmacyNotification,
  getPharmacyUnreadNotificationCount,
  markAllPharmacyNotificationsRead,
  markPharmacyAsReadLocally,
  removePharmacyNotificationLocally,
} from "../../../slice/pharmacy/notifications/pharmacyNotificationsSlice";
import {
  isNotificationUnread,
  getNotificationId,
} from "../../../utils/notificationReadState";
import NotificationsPanelView from "../../notifications/NotificationsPanelView";

const PharmacyNotificationsPanel = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    notifications,
    pagination,
    // eslint-disable-next-line
    unreadCount,
    loading,
    error,
  } = useSelector((state) => state.pharmacyNotifications);

  // Load notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      // Load notifications and unread count
      dispatch(getPharmacyNotifications({ page: 1, limit: 20 }));
      dispatch(getPharmacyUnreadNotificationCount());
    }
  }, [isOpen, dispatch]);

  // Handle mark as read
  // eslint-disable-next-line
  const handleMarkAsRead = async (notificationId) => {
    try {
      // Optimistically update UI
      dispatch(markPharmacyAsReadLocally(notificationId));

      // Make API call
      const result = await dispatch(
        markPharmacyNotificationRead(notificationId),
      ).unwrap();

      if (result.status === true || result.status === "success") {
        // Update unread count
        dispatch(getPharmacyUnreadNotificationCount());
      } else {
        // Revert optimistic update on failure
        toast.error(t("pharmacy.notifications.toast.markReadFailed"));
        // Reload notifications to revert UI state
        dispatch(getPharmacyNotifications({ page: 1, limit: 20 }));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error(t("pharmacy.notifications.toast.markReadFailed"));
      // Reload notifications to revert UI state
      dispatch(getPharmacyNotifications({ page: 1, limit: 20 }));
    }
  };

  // Extract the order id from a notification's data payload (handles both the
  // new-order payload `data.orderId` and the SLA-reminder payload `data.order_id`,
  // and a `data` field that may arrive as a JSON string).
  const getOrderIdFromNotification = (notification) => {
    let data = notification?.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        data = {};
      }
    }
    data = data || {};
    return (
      data.orderId ||
      data.order_id ||
      notification?.orderId ||
      notification?.order_id ||
      null
    );
  };

  // Informational click: mark the notification read and open the related order.
  const handleNotificationClick = async (notification) => {
    const notifId = getNotificationId(notification);

    // Mark as read (only if still unread) — fire-and-forget so navigation stays snappy.
    // The old test was `status === "sent" || is_read === false`, which was true for every
    // delivered notification, so each click re-sent the request for an already-read row.
    if (isNotificationUnread(notification) && notifId) {
      handleMarkAsRead(notifId);
    }

    const orderId = getOrderIdFromNotification(notification);
    onClose();

    if (!orderId) {
      // No linked order (e.g. a generic notice) — just close the panel.
      return;
    }

    // Fetch the full order (same shape the orders list uses) then open its details.
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/pharmacy/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const json = await response.json();
      const order = json?.data;
      if (order) {
        navigate("/pharmacy-order-details", { state: { order } });
      } else {
        navigate("/pharmacy-all-order");
      }
    } catch (e) {
      console.warn("Failed to open order from notification", e);
      navigate("/pharmacy-all-order");
    }
  };

  // Handle delete notification
  const handleDeleteNotification = async (notificationId) => {
    console.log(
      "[PHARMACY_NOTIFICATIONS_PANEL] Delete notification called with ID:",
      notificationId,
    );
    console.log(
      "[PHARMACY_NOTIFICATIONS_PANEL] Current notifications before delete:",
      notifications.length,
    );

    try {
      // Optimistically remove from UI
      console.log(
        "[PHARMACY_NOTIFICATIONS_PANEL] Dispatching removePharmacyNotificationLocally",
      );
      dispatch(removePharmacyNotificationLocally(notificationId));

      // Make API call
      console.log(
        "[PHARMACY_NOTIFICATIONS_PANEL] Making API call to delete notification",
      );
      const result = await dispatch(
        deletePharmacyNotification(notificationId),
      ).unwrap();

      console.log(
        "[PHARMACY_NOTIFICATIONS_PANEL] Delete API response:",
        result,
      );

      if (result.status === true || result.status === "success") {
        // Update unread count
        console.log(
          "[PHARMACY_NOTIFICATIONS_PANEL] Delete successful, updating unread count",
        );
        dispatch(getPharmacyUnreadNotificationCount());
        toast.success(t("pharmacy.notifications.toast.deleted"));
      } else {
        // Revert optimistic update on failure
        console.error(
          "[PHARMACY_NOTIFICATIONS_PANEL] Delete failed, reverting optimistic update",
        );
        toast.error(t("pharmacy.notifications.toast.deleteFailed"));
        // Reload notifications to revert UI state
        dispatch(getPharmacyNotifications({ page: 1, limit: 20 }));
      }
    } catch (error) {
      console.error(
        "[PHARMACY_NOTIFICATIONS_PANEL] Error deleting notification:",
        error,
      );
      toast.error(t("pharmacy.notifications.toast.deleteFailed"));
      // Reload notifications to revert UI state
      dispatch(getPharmacyNotifications({ page: 1, limit: 20 }));
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      const result = await dispatch(
        markAllPharmacyNotificationsRead(),
      ).unwrap();

      if (result.status === true || result.status === "success") {
        // Reload notifications to show updated state
        dispatch(getPharmacyNotifications({ page: 1, limit: 20 }));
        dispatch(getPharmacyUnreadNotificationCount());
        toast.success(t("pharmacy.notifications.toast.allMarkedRead"));
      } else {
        toast.error(t("pharmacy.notifications.toast.markAllReadFailed"));
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error(t("pharmacy.notifications.toast.markAllReadFailed"));
    }
  };

  // Load more notifications
  const loadMore = () => {
    if (!loading.fetch && pagination.page < pagination.totalPages) {
      dispatch(
        getPharmacyNotifications({
          page: pagination.page + 1,
          limit: 20,
        }),
      );
    }
  };

  if (!isOpen) {
    return null;
  }

  const isChatMessage = (notification) =>
    notification.notification_type === "PHARMACY_CHAT_MESSAGE" ||
    notification.notification_type === "CHAT_MESSAGE" ||
    notification.notificationType === "CHAT_MESSAGE" ||
    notification.title?.toLowerCase() === "new message";

  return (
    <NotificationsPanelView
      onClose={onClose}
      labels={{
        title: t("notifications.title"),
        unreadCount: (count) => t("notifications.unreadCount", { count }),
        markAllAsRead: t("pharmacy.notifications.markAllAsRead"),
        loading: t("pharmacy.notifications.loading"),
        failedToLoad: t("pharmacy.notifications.failedToLoad"),
        retry: t("common.retry"),
        empty: t("pharmacy.notifications.noNotifications"),
        emptyHint: t("pharmacy.notifications.noNotificationsHint"),
        delete: t("notifications.deleteNotification"),
        loadMore: t("pharmacy.notifications.loadMore"),
        loadingMore: t("pharmacy.notifications.loadingMore"),
      }}
      notifications={notifications}
      unreadCount={unreadCount}
      loading={loading.fetch}
      error={Boolean(error.fetch)}
      markingAllRead={loading.markAllRead}
      hasMore={pagination.page < pagination.totalPages}
      onRetry={() => dispatch(getPharmacyNotifications({ page: 1, limit: 20 }))}
      onLoadMore={loadMore}
      onMarkAllAsRead={handleMarkAllAsRead}
      getKey={getNotificationId}
      getTitle={(n) => n.title || t("pharmacy.notifications.fallbackTitle")}
      getMessage={(n) =>
        isChatMessage(n) ? t("pharmacy.notifications.newMessageFromAdmin") : n.body
      }
      isUnread={isNotificationUnread}
      onOpen={handleNotificationClick}
      onDelete={(n) => handleDeleteNotification(getNotificationId(n))}
    />
  );
};

export default PharmacyNotificationsPanel;
